import axios from 'axios';
import {
  HorizonInstance,
  HorizonInstanceInput,
  HorizonHostInput,
  HorizonAccessEntry,
  HorizonAccessInstance,
  RecordingSession,
  SessionParticipant,
  ApiResponse,
  ManagedUser,
  LoginAuditEntry,
  AllowlistEntry,
  BlockedAttempt,
  BlockedGeoResponse,
  RecorderStatusResponse,
  ActiveRecordingSession,
  VconSummary,
  VconDocument,
  McpApp,
  McpBlockedAttempt,
  VconLifecycleEvent,
  LedgerVerification,
  ConsentDetail,
  MetricsHistoryResponse,
  RetentionPolicy,
  DispositionResult,
  VconHolder,
  RedactResult, RedactMode, ThreadMember, ThreadSuggestion, ThreadConsent,
  PartyConsentEntry,
  ConsentDomainDefault,
  ConsentInvite,
  PortalView,
  PortalReceiptResult,
  ConsentReceiptSummary,
  VerifiedReceipt,
  AccessLevel,
  ScopeTree,
  TerritoryStats,
  McpAccessEntry,
  McpAccessShape,
  TranscribeResult,
  TranscriptionStats,
  DomainLanguageSaved,
  VconDialogText,
  AskResult,
  AskEngineInfo,
  AskEngineName,
  AskLogSummary,
  AskLogShape,
  AskLogEntry,
  ServiceAnalyticsSummary,
  PersonSummary,
  PersonDetail,
  PersonLinkSuggestion,
} from '../types';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
});

export interface SearchParams {
  page?: number;
  limit?: number;
  search?: string;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
  session_id?: number;
  // UTC "YYYY-MM-DD HH:MM:SS" bounds on start_time (sessions.php)
  start_from?: string;
  start_to?: string;
  // Where in the territory > domain hierarchy to look. Omit (or '*') for
  // everything the account may see. Every listing endpoint accepts these and
  // intersects them with the account's own level, so an out-of-reach value is
  // a 403 rather than an empty page.
  territory?: string;
  domain?: string;
  // vCon-specific narrowing, also accepted by the MCP query tool.
  uuid?: string;
  party?: string;
  // Full-text search over what was said inside the conversation. Unlike
  // `search` this matches whole words, and it re-orders results by relevance.
  content?: string;
  // Ask log: restrict to questions asked through one surface.
  source?: 'console' | 'mcp';
}

// The territory/domain hierarchy, and the MCP access log over it.
export const territoryService = {
  // The tree this account may see, plus its own level. Read once at load and
  // used to populate every scope pulldown.
  tree: async (): Promise<ScopeTree> => {
    const response = await api.get('/territories.php');
    return response.data;
  },

  stats: async (params: SearchParams = {}): Promise<TerritoryStats> => {
    const response = await api.get('/territory_stats.php', { params });
    return response.data;
  },
};

// Service analytics for the Analytics tab.
//
// A longer timeout than the shared client's, because this is a rollup rather
// than a listing: the largest scope here is 96k conversations, and measured on
// the GCP VM it takes about six seconds — most of it window functions over the
// deduplicated set. That is a slow page, not a broken one, and timing it out
// at the default would drop the console back to demonstration figures for a
// request that was about to succeed.
//
// Still no retry. A failure here means the endpoint or its table is genuinely
// absent, and the page has a bundled fixture to fall back to and a badge that
// says so.
export const serviceAnalyticsService = {
  summary: async (params: SearchParams = {}): Promise<ServiceAnalyticsSummary> => {
    const response = await api.get('/analytics_summary.php', { params, timeout: 30000 });
    return response.data;
  },
};

export const transcriptionService = {
  // Just what was said or written — no audio, nothing truncated. The document
  // view can give you one or the other but never both.
  dialogText: async (uuid: string): Promise<VconDialogText> => {
    const response = await api.get('/vcon_dialog_text.php', { params: { uuid } });
    return response.data;
  },

  // Transcribe one recording now, rather than waiting for the nightly run.
  // Slow by web standards — it is a speech-recognition round trip — so callers
  // should show progress rather than assume it returns promptly.
  // `language` overrides the domain's default for this one recording — for the
  // call that is not in the language its switch usually carries. Omitted, the
  // server resolves the domain default.
  transcribe: async (uuid: string, language?: string): Promise<TranscribeResult> => {
    const response = await api.post('/vcon_transcribe.php',
      language ? { uuid, language } : { uuid }, { timeout: 600000 });
    return response.data;
  },

  stats: async (params: SearchParams = {}): Promise<TranscriptionStats> => {
    const response = await api.get('/transcription_stats.php', { params });
    return response.data;
  },

  // Set a domain's transcription language, or clear it (language: '') so the
  // domain follows the deployment default again. `model` likewise: '' means
  // automatic — derived from the language, which is right unless the language
  // has more than one usable model. Omitting it leaves the stored choice
  // alone; the server drops one the new language cannot use and says so.
  setDomainLanguage: async (territory: string, domain: string, language: string,
                            model?: string): Promise<DomainLanguageSaved> => {
    const response = await api.post('/domain_language_update.php',
      model === undefined ? { territory, domain, language } : { territory, domain, language, model });
    return response.data;
  },
};

export interface McpAccessParams extends SearchParams {
  tool?: string;
  app_id?: number;
  status?: 'ok' | 'denied' | 'error';
  min_duration_ms?: number;
  since?: string;
}

export const horizonInstancesService = {
  list: async (): Promise<{ instances: HorizonInstance[]; require_own_secret: boolean }> => {
    const response = await api.get('/horizon_instances_list.php');
    return response.data;
  },

  // Returns the generated callback secret, to paste into that Horizon's
  // Registered Apps page.
  create: async (input: HorizonInstanceInput): Promise<{ id: number; callback_secret: string }> => {
    const response = await api.post('/horizon_instances_create.php', input);
    return response.data;
  },

  // Returns the callback secret when one was set or generated, so it can be
  // pasted into Horizon's Registered Apps page.
  update: async (
    id: number, changes: Partial<HorizonInstanceInput & { status: string }>,
  ): Promise<{ callback_secret?: string }> => {
    const response = await api.post('/horizon_instances_update.php', { id, ...changes });
    return response.data;
  },


  // Add a node to a cluster, or move one between clusters — saving a hostname
  // that already exists re-points it.
  saveHost: async (input: HorizonHostInput): Promise<void> => {
    await api.post('/horizon_instance_host_save.php', input);
  },

  deleteHost: async (id: number, confirmLast = false): Promise<void> => {
    await api.post('/horizon_instance_host_delete.php', { id, confirm_last: confirmLast });
  },
};

export interface HorizonAccessParams extends SearchParams {
  endpoint?: string;
  instance_id?: number;
  party_uid?: string;
  status?: 'ok' | 'denied' | 'error';
  // "Show me only the refusals" — a run of these is what a stolen or stale
  // credential looks like.
  denied_only?: boolean;
  since?: string;
}

export const horizonAccessService = {
  list: async (params: HorizonAccessParams = {}): Promise<{
    entries: HorizonAccessEntry[];
    details_visible: boolean;
    pagination: ApiResponse<HorizonAccessEntry>['pagination'];
  }> => {
    const response = await api.get('/horizon_access_list.php', { params });
    return response.data;
  },

  // One row per (portal, key) — who is calling and whether anything is refused.
  instances: async (params: HorizonAccessParams = {}): Promise<HorizonAccessInstance[]> => {
    const response = await api.get('/horizon_access_list.php', {
      params: { ...params, view: 'instances' },
    });
    return response.data.instances;
  },
};

export const mcpAccessService = {
  list: async (params: McpAccessParams = {}): Promise<{
    entries: McpAccessEntry[];
    params_visible: boolean;
    pagination: ApiResponse<McpAccessEntry>['pagination'];
  }> => {
    const response = await api.get('/mcp_access_list.php', { params });
    return response.data;
  },

  // Calls grouped by query shape, worst waste first.
  shapes: async (params: McpAccessParams = {}): Promise<McpAccessShape[]> => {
    const response = await api.get('/mcp_access_list.php', {
      params: { ...params, view: 'shapes' },
    });
    return response.data.shapes;
  },
};

export const apiService = {
  getSessions: async (params: SearchParams = {}): Promise<ApiResponse<RecordingSession>> => {
    const response = await api.get('/sessions.php', { params });
    return response.data;
  },

  getParticipants: async (params: SearchParams = {}): Promise<ApiResponse<SessionParticipant>> => {
    const response = await api.get('/participants.php', { params });
    return response.data;
  },
};

export const vconService = {
  list: async (params: SearchParams = {}): Promise<ApiResponse<VconSummary>> => {
    const response = await api.get('/vcons.php', { params });
    return response.data;
  },

  // Full vCon document, including the inline base64url audio body the
  // playback modal decodes.
  getVcon: async (filename: string): Promise<VconDocument> => {
    // Longer timeout than the default 10s: the embedded audio makes these
    // documents megabytes big.
    const response = await api.get('/vcon_file.php', { params: { name: filename }, timeout: 120000 });
    return response.data;
  },

  downloadUrl: (filename: string): string =>
    `${API_BASE_URL}/vcon_file.php?name=${encodeURIComponent(filename)}&download=1`,

  // A playable WAV rebuilt from the vCon's inline audio. This is how a
  // recording stays available as a WAV even when the recorder is set to
  // keep only the vCon (delete_wav_after_vcon).
  wavFromVconUrl: (filename: string): string =>
    `${API_BASE_URL}/vcon_wav.php?name=${encodeURIComponent(filename)}&download=1`,

  // The document for the View popup. abbreviated=true truncates inline
  // media bodies server-side so nothing huge crosses the wire by default.
  view: async (filename: string, abbreviated: boolean): Promise<any> => {
    const response = await api.get('/vcon_file.php', {
      params: abbreviated ? { name: filename, abbrev: 1 } : { name: filename },
      timeout: 120000,
    });
    return response.data;
  },

  // The vCon document goes up as the raw request body — see vcon_upload.php
  // for why this beats a multipart upload here.
  upload: async (filename: string, body: string): Promise<{ filename: string; uuid: string }> => {
    const response = await api.post(`/vcon_upload.php?name=${encodeURIComponent(filename)}`, body, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 120000,
    });
    return response.data;
  },

  delete: async (filename: string): Promise<void> => {
    await api.post('/vcon_delete.php', { name: filename });
  },

  // Per-party redaction: identity markers + audio-channel silencing.
  // mode is required rather than defaulted: one of the two is irreversible,
  // and that should never be what happens when a caller says nothing.
  redact: async (filename: string, partyIndex: number, mode: RedactMode): Promise<RedactResult> => {
    const response = await api.post('/vcon_redact.php',
      { name: filename, party: partyIndex, mode }, { timeout: 120000 });
    return response.data;
  },

  // Copies & notifications: which apps hold a copy, notice/purge state.
  holders: async (uuid: string): Promise<VconHolder[]> => {
    const response = await api.get('/vcon_holders.php', { params: { uuid } });
    return response.data.holders;
  },
};

export interface AuthUser {
  email: string;
  name: string;
}

// Asking questions of the recordings in plain English.
//
// One call, and it can take the better part of a minute: the server runs a
// multi-turn agent loop, and there is no streaming yet. The axios default
// timeout is far too short for that, so this call carries its own.
//
// The three ways this legitimately fails have three different statuses and the
// caller is expected to tell them apart — 403 you cannot use it, 429 you have
// spent enough today, 502 the model is unreachable. Collapsing them into "an
// error occurred" would leave someone retrying a question that will never work.
// An error from ask.php that carries its status, so the caller can tell the
// three legitimate failures apart without digging through an axios shape.
// The streaming path does not go through axios at all, so it needs its own.
export class AskError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export type AskEvent =
  | { name: 'thinking'; turn: number }
  | { name: 'tool'; tool: string; args: Record<string, any> }
  | { name: 'tool_done'; tool: string; args: Record<string, any>; ms: number; refused: boolean }
  | { name: 'writing'; reads: number; tool_calls: number }
  | { name: 'done'; result: AskResult };

export const askService = {
  // Streaming ask. Server-sent events over a POST, read with fetch rather than
  // EventSource — EventSource can only issue a GET, and a question can name a
  // recorded party, which is not something to put in a URL where it lands in
  // every access log between here and the server.
  //
  // Falls back cleanly: a failure before the stream starts arrives as an
  // ordinary JSON response with a status, because the endpoint's permission and
  // scope checks run before it commits to the event-stream content type.
  askStream: async (
    question: string,
    scope: { territory?: string; domain?: string } = {},
    asController = false,
    onEvent?: (event: AskEvent) => void,
    engine?: AskEngineName,
    model?: string,
  ): Promise<AskResult> => {
    const response = await fetch('/api/ask.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        question,
        territory: scope.territory,
        domain: scope.domain,
        as_controller: asController,
        engine,
        model,
        stream: true,
      }),
    });

    if (!response.ok || !response.body) {
      let message = 'The question could not be answered.';
      try {
        message = (await response.json())?.error || message;
      } catch { /* a non-JSON body is not more informative than the default */ }
      throw new AskError(message, response.status);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: AskResult | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Frames are separated by a blank line. Anything after the last complete
      // separator is a partial frame and stays in the buffer.
      let split: number;
      while ((split = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        const name = frame.match(/^event: (.*)$/m)?.[1];
        const raw = frame.match(/^data: (.*)$/m)?.[1];
        if (!name || !raw) continue;
        let data: any;
        try { data = JSON.parse(raw); } catch { continue; }

        if (name === 'error') {
          throw new AskError(data.error || 'The question could not be answered.',
                             data.status || 500);
        }
        if (name === 'done') {
          result = data as AskResult;
          onEvent?.({ name: 'done', result: data });
          continue;
        }
        onEvent?.({ name, ...data } as AskEvent);
      }
    }

    if (!result) {
      // The stream ended without a final frame — a dropped connection, or the
      // server dying mid-question. Saying so beats returning an empty answer.
      throw new AskError('The connection ended before an answer arrived.', 502);
    }
    return result;
  },

  ask: async (
    question: string,
    scope: { territory?: string; domain?: string } = {},
    asController = false,
    engine?: AskEngineName,
    model?: string,
  ): Promise<AskResult> => {
    const response = await api.post('/ask.php', {
      question,
      territory: scope.territory,
      domain: scope.domain,
      as_controller: asController,
      engine,
      model,
    }, { timeout: engine === 'local' ? 1800000 : 300000 });
    return response.data;
  },

  // The engines this host offers. One entry on a host with no local model,
  // in which case the Ask tab shows no choice at all.
  engines: async (): Promise<{ default: AskEngineName; engines: AskEngineInfo[] }> => {
    const response = await api.get('/ask.php');
    return response.data;
  },
};

// The Ask log — what the feature is costing and which question shapes cost it.
export const askLogService = {
  summary: async (params: SearchParams = {}): Promise<AskLogSummary> => {
    const response = await api.get('/ask_log_list.php', { params: { ...params, view: 'summary' } });
    return response.data;
  },
  shapes: async (params: SearchParams = {}): Promise<AskLogShape[]> => {
    const response = await api.get('/ask_log_list.php', { params: { ...params, view: 'shapes' } });
    return response.data.shapes;
  },
  list: async (params: SearchParams = {}): Promise<{
    entries: AskLogEntry[];
    pagination: { total_count: number; total_pages: number; current_page: number };
    can_read_questions: boolean;
  }> => {
    const response = await api.get('/ask_log_list.php', { params });
    return response.data;
  },
};

export const authService = {
  login: async (email: string, password: string): Promise<AuthUser> => {
    const response = await api.post('/login.php', { email, password });
    return response.data.user;
  },

  logout: async (): Promise<void> => {
    await api.post('/logout.php');
  },

  getMe: async (): Promise<AuthUser | null> => {
    const response = await api.get('/me.php');
    return response.data.authenticated ? response.data.user : null;
  },
};

export interface NewUserInput {
  email: string;
  name: string;
  password: string;
  allowed_login_ips: string[];
  // How much of the hierarchy the account sees. territory/domain are required
  // by the API for the levels that use them, and ignored for 'super'.
  access_level?: AccessLevel;
  territory?: string;
  domain?: string;
  // Off unless ticked. A new account is not handed a permission that spends
  // money by virtue of having been created.
  can_ask?: boolean;
}

export interface UpdateUserInput {
  id: number;
  name?: string;
  password?: string;
  allowed_login_ips?: string[];
  is_active?: boolean;
  can_ask?: boolean;
  // Sent as a unit: the API rejects a level change that leaves the scope
  // below it unset, so all three travel together or none do.
  access_level?: AccessLevel;
  territory?: string;
  domain?: string;
}

export const settingsService = {
  listUsers: async (): Promise<ManagedUser[]> => {
    const response = await api.get('/users_list.php');
    return response.data.users;
  },

  createUser: async (input: NewUserInput): Promise<void> => {
    await api.post('/users_create.php', input);
  },

  updateUser: async (input: UpdateUserInput): Promise<void> => {
    await api.post('/users_update.php', input);
  },

  deleteUser: async (id: number): Promise<void> => {
    await api.post('/users_delete.php', { id });
  },

  listLoginAudit: async (params: SearchParams = {}): Promise<ApiResponse<LoginAuditEntry>> => {
    const response = await api.get('/login_audit_list.php', { params });
    return response.data;
  },

  listAllowlist: async (): Promise<AllowlistEntry[]> => {
    const response = await api.get('/sip_rtp_allowlist_list.php');
    return response.data.entries;
  },

  addAllowlistEntry: async (cidr: string, description: string): Promise<void> => {
    await api.post('/sip_rtp_allowlist_add.php', { cidr, description });
  },

  updateAllowlistEntry: async (id: number, description: string): Promise<void> => {
    await api.post('/sip_rtp_allowlist_update.php', { id, description });
  },

  deleteAllowlistEntry: async (id: number): Promise<void> => {
    await api.post('/sip_rtp_allowlist_delete.php', { id });
  },

  listBlockedAttempts: async (params: SearchParams = {}): Promise<ApiResponse<BlockedAttempt>> => {
    const response = await api.get('/sip_rtp_blocked_list.php', { params });
    return response.data;
  },

  getBlockedGeo: async (): Promise<BlockedGeoResponse> => {
    const response = await api.get('/sip_rtp_blocked_geo.php');
    return response.data;
  },
};

export interface McpAppInput {
  app_name?: string;
  version?: string;
  vendor?: string;
  description?: string;
  status?: 'active' | 'disabled';
  // Which corpus the key may reach; enforced by mcp.php as an isolation boundary.
  scope?: 'production' | 'experimental';
  allowed_from_ips?: string[];
  contact_email?: string;
  can_query?: boolean;
  can_download?: boolean;
  can_upload?: boolean;
  can_update?: boolean;
  can_consent?: boolean;
  can_lifecycle?: boolean;
  can_ask?: boolean;
  scope_territory?: string;
  scope_domain?: string;
  can_ingest_chat?: boolean;
  can_ingest_email?: boolean;
  can_relay_email?: boolean;
  // People may sign in through this app (OAuth, e.g. a claude.ai connector).
  oauth_enabled?: boolean;
  // Comma-separated; empty = the key's own scope domain.
  internal_domains?: string;
  // Empty = none (the key can then only be posted to directly).
  receiver_address?: string;
}

export const mcpAppsService = {
  list: async (search?: string): Promise<McpApp[]> => {
    const response = await api.get('/mcp_apps_list.php', { params: search ? { search } : {} });
    return response.data.apps;
  },

  listBlocked: async (params: SearchParams = {}): Promise<ApiResponse<McpBlockedAttempt>> => {
    const response = await api.get('/mcp_blocked_list.php', { params });
    return response.data;
  },

  getBlockedGeo: async (): Promise<BlockedGeoResponse> => {
    const response = await api.get('/mcp_blocked_geo.php');
    return response.data;
  },

  // Returns the generated API key (shown once prominently, but it stays
  // retrievable from the list).
  create: async (input: McpAppInput): Promise<{ id: number; api_key: string }> => {
    const response = await api.post('/mcp_apps_create.php', input);
    return response.data;
  },

  update: async (id: number, input: McpAppInput): Promise<void> => {
    await api.post('/mcp_apps_update.php', { id, ...input });
  },

  // Ends every sign-in made through the app; the setting itself is unchanged.
  revokeSignIns: async (id: number): Promise<number> => {
    const response = await api.post('/mcp_apps_oauth_revoke.php', { id });
    return response.data.revoked;
  },

  regenerateKey: async (id: number): Promise<string> => {
    const response = await api.post('/mcp_apps_update.php', { id, regenerate_key: true });
    return response.data.api_key;
  },

  delete: async (id: number): Promise<void> => {
    await api.post('/mcp_apps_delete.php', { id });
  },
};

// How a consent assertion was acquired ('attested' = bare assertion)
export const CONSENT_PROOF_TYPES = [
  'attested', 'a_priori_agreement', 'announcement', 'dtmf', 'verbal', 'portal_receipt',
];

// GDPR Article 6, as draft-howe-vcon-lawful-basis-02 enumerates it. Distinct
// from the proof types above: a basis is WHY processing is lawful, a proof is
// HOW that was established and what can be shown for it.
// The purpose vocabulary, mirroring CONSENT_PURPOSES in consent_common.php.
export const CONSENT_PURPOSES = ['recording', 'transcription', 'analysis', 'ai_training'];

export const CONSENT_LAWFUL_BASES = [
  'consent', 'contract', 'legal_obligation',
  'vital_interests', 'public_task', 'legitimate_interests',
];

// Short, plain-language readings for the console. The enum values are the
// draft's and go over the wire unchanged; these are what a person reads.
export const LAWFUL_BASIS_LABELS: Record<string, string> = {
  consent: 'they agreed',
  contract: 'needed for a contract',
  legal_obligation: 'required by law',
  vital_interests: "someone's life at stake",
  public_task: 'official public function',
  legitimate_interests: 'our interest, with an opt-out',
};

export interface ConsentProof {
  proof_type?: string;
  proof_reference?: string;
  lawful_basis?: string;
}

// Cross-channel threading. Reads are per conversation; the thread-wide consent
// question is separate and asked only when someone wants an answer for the set
// rather than for one of its members.
// People: who is who across calls, email and chat (person-identity plan,
// phase 4). Actions need a territory or super account and a written reason;
// the API refuses anything else with a message worth showing as-is.
export type PersonAction =
  | { action: 'merge'; survivor: string; absorbed: string; reason: string }
  | { action: 'split'; identifier_id: number; reason: string }
  | { action: 'set_shared'; identifier_id: number; shared: boolean; reason: string }
  | { action: 'set_kind'; person_id: string; kind: string; reason: string }
  | { action: 'rename'; person_id: string; name: string; reason: string };

export const peopleService = {
  list: async (search?: string, scope: { territory?: string; domain?: string } = {}):
      Promise<{ people: PersonSummary[]; suggestions: PersonLinkSuggestion[]; can_edit: boolean }> => {
    const r = await api.get('/people_list.php', { params: { ...(search ? { search } : {}), ...scope } });
    return r.data;
  },
  get: async (id: string): Promise<PersonDetail> => {
    const r = await api.get('/person_get.php', { params: { id } });
    return r.data;
  },
  act: async (body: PersonAction): Promise<{ success: boolean; person_id?: string }> => {
    const r = await api.post('/person_action.php', body);
    return r.data;
  },
};

export const threadService = {
  // Every thread visible in the caller's scope, summarised.
  list: async (territory?: string, domain?: string): Promise<unknown[]> => {
    const r = await api.get('/vcon_thread.php', {
      params: { list: 1, ...(territory ? { territory } : {}), ...(domain ? { domain } : {}) },
    });
    return r.data.threads || [];
  },

  byId: async (threadId: string): Promise<ThreadMember[]> => {
    const r = await api.get('/vcon_thread.php', { params: { thread_id: threadId } });
    return r.data.members || [];
  },

  forVcon: async (uuid: string): Promise<{ thread_id: string; members: ThreadMember[] }[]> => {
    const r = await api.get('/vcon_thread.php', { params: { uuid } });
    return r.data.threads || [];
  },

  suggestions: async (uuid: string, windowDays = 7): Promise<ThreadSuggestion[]> => {
    const r = await api.get('/vcon_thread.php', { params: { suggest: uuid, window_days: windowDays } });
    return r.data.suggestions || [];
  },

  consent: async (threadId: string, purpose: string): Promise<ThreadConsent> => {
    const r = await api.get('/vcon_thread.php', { params: { thread_id: threadId, consent: purpose } });
    return r.data.thread_consent;
  },

  // thread_id omitted mints one. Returns the id used, so the caller can show
  // it without guessing.
  link: async (uuids: string[], threadId?: string): Promise<{ thread_id: string; members: ThreadMember[] }> => {
    const r = await api.post('/vcon_thread.php', { action: 'link', uuids, ...(threadId ? { thread_id: threadId } : {}) });
    return r.data;
  },

  unlink: async (threadId: string, uuid: string): Promise<void> => {
    await api.post('/vcon_thread.php', { action: 'unlink', thread_id: threadId, uuid });
  },
};

export const consentService = {
  get: async (filename: string): Promise<ConsentDetail> => {
    const response = await api.get('/vcon_consent_get.php', { params: { name: filename } });
    return response.data;
  },

  set: async (filename: string, action: 'grant' | 'revoke', partyIndex: number,
              purposes: string[], proof?: ConsentProof): Promise<void> => {
    await api.post('/vcon_consent_set.php', {
      name: filename,
      action,
      party_index: partyIndex,
      purposes,
      ...(proof || {}),
    });
  },
};

// Consent flag payloads share the DB column names.
export interface ConsentFlags {
  consent_recording?: boolean;
  consent_transcription?: boolean;
  consent_analysis?: boolean;
  consent_ai_training?: boolean;
}

export const consentAdminService = {
  listParties: async (search?: string, scope: { territory?: string; domain?: string } = {}): Promise<PartyConsentEntry[]> => {
    const response = await api.get('/party_consent_list.php', {
      params: { ...(search ? { search } : {}), ...scope },
    });
    return response.data.parties;
  },

  updateParty: async (id: number, changes: ConsentFlags & { party_name?: string } & ConsentProof): Promise<void> => {
    await api.post('/party_consent_update.php', { id, ...changes });
  },

  // flags omitted = the new entry takes its domain's defaults (source
  // stays "auto"); flags given = explicit choices (source becomes "admin").
  addParty: async (party_tel: string, party_name: string, flags?: ConsentFlags): Promise<void> => {
    await api.post('/party_consent_update.php', { add: true, party_tel, party_name, ...(flags || {}) });
  },

  deleteParty: async (id: number): Promise<void> => {
    await api.post('/party_consent_update.php', { id, delete: true });
  },

  // Registers every party appearing in the existing vCon archive
  backfill: async (): Promise<{ new_parties_registered: number; total_parties: number }> => {
    const response = await api.post('/party_consent_backfill.php', {}, { timeout: 120000 });
    return response.data;
  },

  listDomains: async (search?: string): Promise<ConsentDomainDefault[]> => {
    const response = await api.get('/consent_domains.php', { params: search ? { search } : {} });
    return response.data.domains;
  },

  saveDomain: async (domain: string, flags: ConsentFlags, proof?: ConsentProof): Promise<void> => {
    await api.post('/consent_domains.php', { domain, ...flags, ...(proof || {}) });
  },

  deleteDomain: async (domain: string): Promise<void> => {
    await api.post('/consent_domains.php', { domain, delete: true });
  },
};

// Status page history charts — all data served from the platform's own DB
export const metricsService = {
  history: async (params: { window?: string; from?: string; to?: string }): Promise<MetricsHistoryResponse> => {
    const response = await api.get('/metrics_history.php', { params });
    return response.data;
  },
};

// Retention/expiry policy and the disposition run (Settings -> Retention)
export const dispositionService = {
  listPolicies: async (): Promise<RetentionPolicy[]> => {
    const response = await api.get('/disposition_policy.php');
    return response.data.policies;
  },

  savePolicy: async (domain: string, retention_days: number | null, delete_wav: boolean): Promise<void> => {
    await api.post('/disposition_policy.php', { domain, retention_days, delete_wav });
  },

  deletePolicy: async (domain: string): Promise<void> => {
    await api.post('/disposition_policy.php', { domain, delete: true });
  },

  run: async (dryRun: boolean): Promise<DispositionResult> => {
    const response = await api.post('/disposition_run.php', { dry_run: dryRun }, { timeout: 300000 });
    return response.data;
  },
};

// Console-side management of portal consent invites
export const portalAdminService = {
  listInvites: async (search?: string): Promise<ConsentInvite[]> => {
    const response = await api.get('/consent_invites_admin.php', { params: search ? { search } : {} });
    return response.data.invites;
  },

  // party_uid is the subject; party_tel is only the device they were last
  // seen on. An invite without the uid produces a receipt (and a portal page)
  // that names a handset instead of a person.
  createInvite: async (party_tel: string, party_name: string, purposes: string[],
                       expires_days = 7,
                       party_uid?: string | null): Promise<{ token: string; portal_path: string }> => {
    const response = await api.post('/consent_invites_admin.php',
      { party_tel, party_uid: party_uid ?? '', party_name, purposes, expires_days });
    return response.data;
  },

  cancelInvite: async (id: number): Promise<void> => {
    await api.post('/consent_invites_admin.php', { id, cancel: true });
  },

  listReceipts: async (search?: string): Promise<ConsentReceiptSummary[]> => {
    const response = await api.get('/consent_receipts_admin.php', { params: search ? { search } : {} });
    return response.data.receipts;
  },
};

// Fetch + client-side download of a verified receipt bundle (public
// endpoint — also used by the portal for re-downloads).
export const fetchReceipt = async (receiptId: string): Promise<VerifiedReceipt> => {
  const response = await api.get('/portal_receipt_verify.php', { params: { receipt_id: receiptId } });
  return response.data;
};

export const downloadReceiptBundle = (verified: VerifiedReceipt) => {
  const bundle = {
    receipt: verified.receipt,
    signature_b64: verified.signature_b64,
    public_key_b64: verified.public_key_b64,
    algorithm: verified.algorithm,
    verify: 'Ed25519-verify(signature, receipt_json bytes, public_key); or GET /api/portal_receipt_verify.php?receipt_id=...',
  };
  const url = URL.createObjectURL(new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${verified.receipt.receipt_id}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// The public portal endpoints (token-gated; no session)
// The portal's "other ways to reach me" (portal_identity.php). Addresses come
// back masked: a consent link can be forwarded, so holding one shows only
// enough to recognise an address, never to harvest it.
export interface PortalIdentityView {
  identifiers: Array<{ id: number; scheme: string; value: string; proven: boolean; proof: string | null; can_prove: boolean }>;
  proven: boolean;
  anchor_id: number | null;
  channels: { email: boolean; matrix: boolean };
}

export const portalIdentityService = {
  view: async (token: string): Promise<PortalIdentityView> =>
    (await api.get('/portal_identity.php', { params: { token } })).data,
  start: async (token: string, address: string): Promise<{ challenge_id: string; channel: string; masked: string }> =>
    (await api.post('/portal_identity.php', { token, step: 'start', address }, { timeout: 30000 })).data,
  verify: async (token: string, challenge_id: string, code: string): Promise<PortalIdentityView> =>
    (await api.post('/portal_identity.php', { token, step: 'verify', challenge_id, code })).data,
  unlink: async (token: string, identifier_id: number): Promise<PortalIdentityView> =>
    (await api.post('/portal_identity.php', { token, step: 'unlink', identifier_id })).data,
};

export const portalService = {
  view: async (token: string): Promise<PortalView> => {
    const response = await api.get('/portal_consent.php', { params: { token } });
    return response.data;
  },

  submit: async (token: string, action: 'grant' | 'revoke', purposes: string[],
                 applyExisting: boolean): Promise<PortalReceiptResult> => {
    const response = await api.post('/portal_consent.php', {
      token, action, purposes, apply_existing: applyExisting,
    }, { timeout: 120000 });
    return response.data;
  },
};

export interface LifecycleParams extends SearchParams {
  event?: string;
  vcon_uuid?: string;
}

export const lifecycleService = {
  list: async (params: LifecycleParams = {}): Promise<ApiResponse<VconLifecycleEvent>> => {
    const response = await api.get('/vcon_lifecycle_list.php', { params });
    return response.data;
  },

  // Walks the whole chain server-side, recomputing every hash.
  verify: async (): Promise<LedgerVerification> => {
    const response = await api.get('/vcon_lifecycle_list.php', { params: { verify: 1 } });
    return response.data.verification;
  },
};

export const statusService = {
  getRecorderStatus: async (): Promise<RecorderStatusResponse> => {
    const response = await api.get('/recorder_status.php');
    return response.data;
  },

  getActiveRecordings: async (): Promise<ActiveRecordingSession[]> => {
    const response = await api.get('/active_recordings.php');
    return response.data.sessions || [];
  },

  requestRestart: async (): Promise<void> => {
    await api.post('/recorder_restart.php');
  },

  // Only for a recorder that is down: restart is handled by the running
  // process, a cold start by a launchd agent watching for this request.
  requestStart: async (): Promise<void> => {
    await api.post('/recorder_start.php');
  },
};